import { cssVar } from '@mfw/common';
import type { Theme } from '@mui/material/styles';

import { formHelperTextClasses } from '@mui/material/FormHelperText';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { filledInputClasses } from '@mui/material/FilledInput';
import { formLabelClasses } from '@mui/material/FormLabel';
import { inputLabelClasses } from '@mui/material/InputLabel';
import { inputBaseClasses } from '@mui/material/InputBase';
import { inputClasses } from '@mui/material/Input';
import { alpha } from '@mui/material/styles';

import { onSurface } from '../../onSurface';

// ----------------------------------------------------------------------

export function textField(theme: Theme) {
  const color = {
    active: cssVar.text.secondary,
    // The focus ring is the app's highest-frequency brand moment — every form,
    // every page — and it was painted with the text ladder, which is what made
    // a focused field indistinguishable from plain ink. MUI's own default here
    // is `primary.main`; this is that brand at the step measured against the
    // CURRENT surface, so it survives a tenant with a light ladder where
    // `primary.main` reads 1.74:1.
    focused: onSurface.primary,
    // Everything a field in error draws in red IS the red: `error.main` is the
    // fill of a red button, 2.6:1 as a 12px label, an asterisk or a line of
    // helper text and 1.6:1 as a 1px outline on a light tenant. The step
    // measured against the CURRENT surface, used in every place MUI paints
    // it: the shrunk label, the resting label of an empty field (the usual
    // state after a failed submit), the required asterisk, the helper text,
    // the outlined variant's outline and the standard and filled variants'
    // underline (their outline: both its resting `::before` and focused
    // `::after` line). The washes behind a filled input in error
    // (`alpha(error.main, …)`) are fills and stay.
    error: onSurface.error,
    // NOT `text.disabled`: that is an alpha step tuned for inactive controls,
    // which WCAG 1.4.3 exempts — 3.8:1 on a light ladder. A resting label and a
    // placeholder are content the user has to read before they can fill the
    // field in. `text.disabled` stays where it belongs, on disabled rows.
    placeholder: cssVar.text.secondary,
  };

  const font = {
    label: theme.typography.body1,
    value: theme.typography.body2,
  };

  return {
    // FILLED
    MuiFilledInput: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha(theme.palette.grey[500], 0.16),
          },
          [`&.${filledInputClasses.disabled}`]: {
            backgroundColor: theme.palette.action.disabledBackground,
          },
          [`&.${filledInputClasses.error}`]: {
            // MUI's own rule, same selector and specificity: this wins by
            // coming after it.
            '&::before, &::after': {
              borderBottomColor: color.error,
            },
            [`&.${filledInputClasses.focused}`]: {
              backgroundColor: alpha(theme.palette.error.main, 0.16),
            },
            backgroundColor: alpha(theme.palette.error.main, 0.08),
          },
          [`&.${filledInputClasses.focused}`]: {
            backgroundColor: alpha(theme.palette.grey[500], 0.16),
          },
          backgroundColor: alpha(theme.palette.grey[500], 0.08),
          borderRadius: theme.shape.borderRadius,
        },
      },
    },

    // HELPER
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          // MUI's own `.Mui-error { color: error.main }` has the same
          // specificity, so this wins by coming after it.
          [`&.${formHelperTextClasses.error}`]: {
            color: color.error,
          },
          marginTop: theme.spacing(1),
        },
      },
    },

    // LABEL
    MuiFormLabel: {
      styleOverrides: {
        // The `*` of a required field in error: MUI paints it `error.main` on
        // its own, apart from the label.
        asterisk: {
          [`&.${formLabelClasses.error}`]: {
            color: color.error,
          },
        },
        root: {
          ...font.value,
          [`&.${inputLabelClasses.shrink}`]: {
            ...font.label,
            [`&.${inputLabelClasses.disabled}`]: {
              color: cssVar.text.disabled,
            },
            [`&.${inputLabelClasses.error}`]: {
              color: color.error,
            },
            [`&.${inputLabelClasses.filled}`]: {
              transform: 'translate(12px, 6px) scale(0.75)',
            },
            [`&.${inputLabelClasses.focused}`]: {
              color: color.focused,
            },
            color: color.active,
            fontWeight: 600,
          },
          color: color.placeholder,
          // The resting label of an empty field in error. The shrunk label has
          // its own rule above; without this one the placeholder colour here
          // loses to MUI's `.Mui-error { color: error.main }`.
          [`&.${formLabelClasses.error}`]: {
            color: color.error,
          },
        },
      },
    },

    // STANDARD
    MuiInput: {
      styleOverrides: {
        underline: {
          '&:after': {
            borderBottomColor: color.focused,
          },
          '&:before': {
            borderBottomColor: alpha(theme.palette.grey[500], 0.32),
          },
          // MUI's own rule, same selector and specificity: this wins by
          // coming after it.
          [`&.${inputClasses.error}`]: {
            '&::before, &::after': {
              borderBottomColor: color.error,
            },
          },
        },
      },
    },

    // BASE
    MuiInputBase: {
      styleOverrides: {
        input: {
          ...font.value,
          '&::placeholder': {
            color: color.placeholder,
            opacity: 1,
          },
        },
        root: {
          [`&.${inputBaseClasses.disabled}`]: {
            '& svg': {
              color: cssVar.text.disabled,
            },
          },
        },
      },
    },

    // OUTLINED
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: alpha(theme.palette.grey[500], 0.2),
          transition: theme.transitions.create(['border-color'], {
            duration: theme.transitions.duration.shortest,
          }),
        },
        root: {
          [`&.${outlinedInputClasses.disabled}`]: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: theme.palette.action.disabledBackground,
            },
          },
          [`&.${outlinedInputClasses.error}`]: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: color.error,
            },
          },
          [`&.${outlinedInputClasses.focused}`]: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: color.focused,
            },
          },
        },
      },
    },
  };
}
